package ai.beakman.undercover.web

import ai.beakman.undercover.Identity
import ai.beakman.undercover.Invite
import ai.beakman.undercover.data.RoomRepository
import ai.beakman.undercover.data.Themes
import ai.beakman.undercover.game.Avatars
import ai.beakman.undercover.game.GameLogic
import ai.beakman.undercover.i18n.I18n
import ai.beakman.undercover.model.Player
import ai.beakman.undercover.model.Room
import ai.beakman.undercover.model.Stage
import ai.beakman.undercover.model.Vote
import ai.beakman.undercover.online.OnlineGameState
import ai.beakman.undercover.online.nowMillis
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLTextAreaElement

/**
 * Modo Online (multiplayer Firestore realtime). Porte web das telas de
 * shared/.../online/{OnlineApp,OnlineLobby,OnlineReveal,OnlineTimer,OnlineVote,OnlineResult}.kt,
 * reusando o [OnlineGameState] do core. O host dirige as transições de stage.
 */
@Composable
fun OnlineFlow(initialCode: String?, onExit: () -> Unit) {
    // GOTCHA (real do projeto): o scope que alimenta os Flows do Firestore precisa viver
    // aqui no OnlineFlow, que permanece composto durante TODA a sessão online. Se viver
    // numa tela interna, os listeners são cancelados ao trocar de tela.
    val scope = rememberCoroutineScope()
    var state by remember { mutableStateOf<OnlineGameState?>(null) }

    val current = state
    if (current == null) {
        OnlineEntry(
            scope = scope,
            initialCode = initialCode,
            onExit = onExit,
            onState = { state = it },
        )
    } else {
        OnlineRoom(state = current, onLeave = { state = null })
    }
}

// --- Entrada (menu / criar / entrar) -------------------------------------------------

private enum class EntryMode { MENU, CREATE, JOIN }

@Composable
private fun OnlineEntry(
    scope: CoroutineScope,
    initialCode: String?,
    onExit: () -> Unit,
    onState: (OnlineGameState) -> Unit,
) {
    // Deep-link (/sala/CODIGO): vai direto pra tela de entrar, só pedindo o nome.
    var mode by remember { mutableStateOf(if (initialCode != null) EntryMode.JOIN else EntryMode.MENU) }

    when (mode) {
        EntryMode.CREATE -> CreateRoomScreen(
            scope = scope,
            onBack = { mode = EntryMode.MENU },
            onState = onState,
        )
        EntryMode.JOIN -> JoinRoomScreen(
            scope = scope,
            initialCode = initialCode,
            onBack = { mode = EntryMode.MENU },
            onState = onState,
        )
        EntryMode.MENU -> OnlineMenuScreen(
            onCreate = { mode = EntryMode.CREATE },
            onJoin = { mode = EntryMode.JOIN },
            onExit = onExit,
        )
    }
}

@Composable
private fun OnlineMenuScreen(onCreate: () -> Unit, onJoin: () -> Unit, onExit: () -> Unit) {
    val s = I18n.strings
    Div({ classes("screen-body", "center") }) {
        Div({ classes("big-emoji") }) { Text("🌐") }
        Label(s.onlineModeLabel)
        MutedText(s.onlineMenuSubtitle)
        VSpace(12)
        OptionCard(emoji = "🎉", title = s.createRoomCardTitle, subtitle = s.createRoomCardSubtitle, onClick = onCreate)
        OptionCard(emoji = "🔑", title = s.joinRoomCardTitle, subtitle = s.joinRoomCardSubtitle, onClick = onJoin)
        VSpace(12)
        SecondaryButton(s.backArrowVoltar) { onExit() }
    }
}

@Composable
private fun OptionCard(emoji: String, title: String, subtitle: String, onClick: () -> Unit) {
    Button({
        classes("mode-card")
        onClick { onClick() }
    }) {
        Span({ classes("mode-emoji") }) { Text(emoji) }
        Div({ classes("grow") }) {
            Div({ classes("mode-title") }) { Text(title) }
            Div({ classes("mode-subtitle") }) { Text(subtitle) }
        }
        Span({
            style {
                property("color", "var(--primary)")
                property("font-size", "22px")
                property("font-weight", "700")
            }
        }) { Text("→") }
    }
}

@Composable
private fun CreateRoomScreen(
    scope: CoroutineScope,
    onBack: () -> Unit,
    onState: (OnlineGameState) -> Unit,
) {
    val s = I18n.strings
    var nome by remember { mutableStateOf(Identity.savedName ?: "") }
    var themeId by remember { mutableStateOf(Themes.localized(I18n.current).first().id) }
    var creating by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val nomeOk = nome.trim().isNotEmpty()

    Div({ classes("screen-body", "stack") }) {
        Div { BackLink(s.backVoltar) { onBack() } }
        H2({ classes("title") }) { Text(s.createRoomCardTitle) }
        MutedText(s.createRoomDesc)
        TextField(nome, s.yourNamePlaceholder) { nome = it.take(20) }
        VSpace(4)
        Label(s.themeLabel)
        Div({ classes("grid2") }) {
            Themes.localized(I18n.current).forEach { t ->
                val selected = t.id == themeId
                Button({
                    classes("card-select")
                    if (selected) classes("selected")
                    onClick { themeId = t.id }
                }) {
                    Div({ style { property("font-size", "30px") } }) { Text(t.emoji) }
                    Div({
                        style {
                            property("font-size", "14px")
                            property("font-weight", "500")
                            property("margin-top", "6px")
                        }
                    }) { Text(t.name) }
                }
            }
        }
        VSpace(4)
        error?.let { msg -> InlineError(msg) }
        // Loading que SEMPRE resolve: sucesso navega pra sala; falha mostra erro e
        // reabilita o botão (tocar de novo = tentar de novo).
        PrimaryButton(if (creating) s.createRoomCreating else s.createRoomCardTitle, enabled = nomeOk && !creating) {
            val name = nome.trim()
            creating = true
            error = null
            scope.launch {
                try {
                    Identity.saveName(name)
                    val code = GameLogic.generateRoomCode()
                    val gameState = OnlineGameState(code, scope)
                    gameState.createRoom(name, Themes.byId(themeId)!!, 1, 180)
                    onState(gameState)
                } catch (e: Throwable) {
                    error = s.createRoomError
                    creating = false
                }
            }
        }
        VSpace(8)
    }
}

@Composable
private fun JoinRoomScreen(
    scope: CoroutineScope,
    initialCode: String?,
    onBack: () -> Unit,
    onState: (OnlineGameState) -> Unit,
) {
    val s = I18n.strings
    var codigo by remember { mutableStateOf(initialCode ?: "") }
    var nome by remember { mutableStateOf(Identity.savedName ?: "") }
    var joining by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val repo = remember { RoomRepository() }
    val codigoOk = codigo.length >= 4
    val nomeOk = nome.trim().isNotEmpty()

    Div({ classes("screen-body", "stack") }) {
        Div { BackLink(s.backVoltar) { onBack() } }
        H2({ classes("title") }) { Text(s.joinRoomCardTitle) }
        MutedText(s.joinRoomDesc)
        TextField(codigo, s.codePlaceholder) { v ->
            codigo = v.filter { !it.isWhitespace() }.uppercase().take(5)
            error = null
        }
        TextField(nome, s.yourNamePlaceholder) { nome = it.take(20) }
        error?.let { msg -> InlineError(msg) }
        Div({ classes("grow") })
        // Loading que SEMPRE resolve: valida a sala, entra (suspend) e só então navega.
        // Qualquer falha (rede, Firestore) vira erro amigável + botão reabilitado.
        PrimaryButton(if (joining) s.joinEntering else s.joinEnter, enabled = codigoOk && nomeOk && !joining) {
            val code = codigo.trim().uppercase()
            val name = nome.trim()
            joining = true
            error = null
            scope.launch {
                try {
                    if (!repo.roomExists(code)) {
                        error = s.joinRoomNotFound(code)
                        joining = false
                        return@launch
                    }
                    Identity.saveName(name)
                    val gameState = OnlineGameState(code, scope)
                    gameState.join(name)
                    onState(gameState)
                } catch (e: Throwable) {
                    error = s.joinRoomError
                    joining = false
                }
            }
        }
        VSpace(8)
    }
}

// --- Sala (router por stage) -----------------------------------------------------------

/**
 * Executa as ações do Firestore (ready, transições de host, voto...) com tratamento de
 * exceção: loading que SEMPRE resolve (finally) + erro amigável pt-BR exibido em banner.
 * "Tentar de novo" = tocar o mesmo botão de novo (reabilitado quando `busy` volta a false).
 */
private class RoomActions(private val scope: CoroutineScope) {
    var busy by mutableStateOf(false)
        private set
    var error by mutableStateOf<String?>(null)
        private set

    fun runAction(block: suspend () -> Unit) {
        if (busy) return
        busy = true
        error = null
        scope.launch {
            try {
                block()
            } catch (e: Throwable) {
                error = I18n.strings.actionSyncError
            } finally {
                busy = false
            }
        }
    }

    fun dismissError() {
        error = null
    }
}

@Composable
private fun OnlineRoom(state: OnlineGameState, onLeave: () -> Unit) {
    val room by state.room.collectAsState()
    val players by state.players.collectAsState()
    val votes by state.votes.collectAsState()
    val actions = remember(state) { RoomActions(state.scope) }

    // Distingue "ainda carregando" de "o host encerrou a sala" (doc já carregado sumiu).
    var roomEverLoaded by remember(state) { mutableStateOf(false) }
    LaunchedEffect(room != null) {
        if (room != null) roomEverLoaded = true
    }

    val activeRoom = room

    // Saída explícita: avisa o Firestore (host no LOBBY encerra a sala pra todos; senão
    // remove só o próprio jogador) e então reseta a UI. Mesmo com erro de rede, sai.
    var leaving by remember(state) { mutableStateOf(false) }
    fun leaveAndExit() {
        if (leaving) return
        leaving = true
        state.scope.launch {
            try {
                state.leave(activeRoom)
            } catch (e: Throwable) {
                // Sem rede etc. — sai da sala localmente mesmo assim.
            }
            onLeave()
        }
    }

    if (activeRoom == null) {
        val s = I18n.strings
        Div({ classes("screen-body", "center") }) {
            if (roomEverLoaded) {
                // A sala já esteve carregada e o doc sumiu → o host encerrou.
                Div({ classes("big-emoji") }) { Text("🚪") }
                MutedText(s.roomClosed)
                VSpace(12)
                SecondaryButton(s.backArrowVoltar) { onLeave() }
            } else {
                Div({ classes("big-emoji") }) { Text("🔌") }
                MutedText(s.loadingRoom)
                VSpace(12)
                SecondaryButton(s.backArrowSair) { leaveAndExit() }
            }
        }
        return
    }

    val isHost = state.isHost(activeRoom)
    when (activeRoom.stage) {
        Stage.LOBBY -> WebLobby(state, actions, activeRoom, players, isHost, ::leaveAndExit)
        Stage.REVEAL -> WebReveal(state, actions, activeRoom, players, isHost, ::leaveAndExit)
        Stage.TIMER -> WebTimer(state, actions, activeRoom, isHost, ::leaveAndExit)
        Stage.VOTE -> WebVote(state, actions, activeRoom, players, votes, isHost, ::leaveAndExit)
        Stage.RESULT -> WebResult(state, actions, activeRoom, players, votes, isHost, ::leaveAndExit)
    }
    ActionErrorBanner(actions.error) { actions.dismissError() }
}

/** Banner flutuante de erro de ação (toque para dispensar; o botão da ação segue ativo). */
@Composable
private fun ActionErrorBanner(message: String?, onDismiss: () -> Unit) {
    if (message == null) return
    Div({
        classes("danger-text")
        style {
            property("position", "fixed")
            property("left", "50%")
            property("transform", "translateX(-50%)")
            property("bottom", "18px")
            property("width", "calc(100% - 32px)")
            property("max-width", "420px")
            property("background", "var(--card)")
            property("border", "1px solid var(--danger)")
            property("border-radius", "12px")
            property("padding", "10px 14px")
            property("font-size", "13px")
            property("text-align", "center")
            property("z-index", "50")
            property("cursor", "pointer")
        }
        onClick { onDismiss() }
    }) { Text(message) }
}

/** Mensagem de erro inline (telas de criar/entrar). */
@Composable
private fun InlineError(message: String) {
    Div({
        classes("danger-text", "text-center")
        style { property("font-size", "13px") }
    }) { Text(message) }
}

// --- LOBBY ------------------------------------------------------------------------------

@Composable
private fun WebLobby(
    state: OnlineGameState,
    actions: RoomActions,
    room: Room,
    players: List<Player>,
    isHost: Boolean,
    onLeave: () -> Unit,
) {
    val s = I18n.strings
    val maxInf = GameLogic.maxInfiltrators(players.size)
    val enoughPlayers = players.size >= GameLogic.MIN_PLAYERS

    // Scope só pra UI local (toast de "Copiado!") — os Flows usam o scope do OnlineFlow.
    val uiScope = rememberCoroutineScope()
    var copied by remember { mutableStateOf<String?>(null) }
    fun copy(text: String, which: String) {
        copyToClipboard(text) { ok ->
            if (ok) {
                copied = which
                uiScope.launch {
                    delay(1500)
                    if (copied == which) copied = null
                }
            }
        }
    }

    Div({ classes("screen-body", "stack") }) {
        GameTopBar(s.roomLabel, onLeave)

        VSpace(8)
        Label(s.roomCodeLabel)
        Div({
            style {
                property("color", "var(--primary)")
                property("font-size", "44px")
                property("font-weight", "900")
                property("letter-spacing", "10px")
                property("text-align", "center")
                property("padding-left", "10px") // compensa o letter-spacing do último caractere
            }
        }) { Text(room.code) }

        Div({ classes("row") }) {
            Div({ classes("grow") }) {
                // navigator.share quando disponível (mobile); senão copia a mensagem inteira.
                SecondaryButton(if (copied == "invite") s.inviteCopiedConfirm else s.inviteButton) {
                    val message = s.inviteMessage(room.code, Invite.WEB_BASE_URL)
                    if (canNativeShare()) nativeShare(message) else copy(message, "invite")
                }
            }
            Div({ classes("grow") }) {
                SecondaryButton(if (copied == "code") s.copiedWeb else s.copyCode) {
                    copy(room.code, "code")
                }
            }
        }

        VSpace(8)
        CardBox {
            Label(s.playersCount(players.size))
            VSpace(12)
            Div({ classes("stack") }) {
                players.forEach { player ->
                    LobbyPlayerRow(
                        player = player,
                        isHostPlayer = player.clientId == room.hostClientId,
                        isMe = player.clientId == state.clientId,
                    )
                }
            }
        }

        CardBox {
            Div({ classes("stack") }) {
                ConfigRow(s.themeLabel, "${room.themeEmoji} ${room.themeName}")
                Div({ classes("row-between") }) {
                    ConfigLabel(s.infiltratorsTitle)
                    if (isHost) {
                        StepperRow(
                            value = room.numInfiltrators.toString(),
                            onMinus = {
                                actions.runAction { state.setInfiltrators(room, (room.numInfiltrators - 1).coerceAtLeast(1)) }
                            },
                            onPlus = {
                                val mx = GameLogic.maxInfiltrators(players.size)
                                actions.runAction { state.setInfiltrators(room, (room.numInfiltrators + 1).coerceAtMost(mx)) }
                            },
                        )
                    } else {
                        ConfigValue(room.numInfiltrators.toString())
                    }
                }
                ConfigRow(s.timeLabel, formatMmSsOnline(room.timerSeconds))
            }
        }

        VSpace(8)
        if (isHost) {
            PrimaryButton(
                text = if (actions.busy) s.lobbyStartingWeb else s.lobbyStartWeb,
                enabled = enoughPlayers && room.numInfiltrators <= maxInf && !actions.busy,
            ) {
                actions.runAction { state.startGame(room, players) }
            }
            if (!enoughPlayers) {
                Div({ classes("muted", "text-center") }) { Text(s.waitingPlayersMin3) }
            }
        } else {
            Div({ classes("muted", "text-center") }) { Text(s.waitingHostStartWeb) }
        }
        VSpace(16)
    }
}

@Composable
private fun LobbyPlayerRow(player: Player, isHostPlayer: Boolean, isMe: Boolean) {
    Div({ classes("row") }) {
        AvatarBadge(player.name)
        Div({
            classes("grow")
            style {
                property("font-size", "16px")
                property("font-weight", "500")
                property("text-align", "left")
            }
        }) {
            Text(player.name)
            if (isMe) {
                Span({
                    style {
                        property("color", "var(--muted)")
                        property("font-size", "12px")
                        property("margin-left", "6px")
                    }
                }) { Text(I18n.strings.playerYouSuffix) }
            }
        }
        if (isHostPlayer) {
            Span({
                style {
                    property("background", "color-mix(in srgb, var(--primary) 20%, transparent)")
                    property("color", "var(--primary)")
                    property("border-radius", "var(--radius-pill)")
                    property("padding", "4px 10px")
                    property("font-size", "10px")
                    property("font-weight", "700")
                    property("letter-spacing", "1px")
                    property("white-space", "nowrap")
                }
            }) { Text(I18n.strings.hostBadgeWeb) }
        }
    }
}

@Composable
private fun ConfigRow(label: String, value: String) {
    Div({ classes("row-between") }) {
        ConfigLabel(label)
        ConfigValue(value)
    }
}

@Composable
private fun ConfigLabel(text: String) {
    Span({
        style {
            property("color", "var(--muted)")
            property("font-size", "11px")
            property("letter-spacing", "1px")
            property("text-transform", "uppercase")
        }
    }) { Text(text) }
}

@Composable
private fun ConfigValue(text: String) {
    Span({
        style {
            property("font-size", "15px")
            property("font-weight", "500")
        }
    }) { Text(text) }
}

@Composable
private fun StepperRow(value: String, onMinus: () -> Unit, onPlus: () -> Unit) {
    Div({
        classes("row")
        style { property("gap", "14px") }
    }) {
        StepButton("−", onMinus)
        Span({
            style {
                property("font-size", "16px")
                property("font-weight", "700")
                property("min-width", "24px")
                property("text-align", "center")
            }
        }) { Text(value) }
        StepButton("+", onPlus)
    }
}

@Composable
private fun StepButton(symbol: String, onClick: () -> Unit) {
    Button({
        style {
            property("width", "36px")
            property("height", "36px")
            property("border-radius", "var(--radius-pill)")
            property("border", "1px solid var(--border)")
            property("background", "var(--secondary)")
            property("color", "var(--on-secondary)")
            property("font-size", "18px")
            property("font-family", "inherit")
            property("cursor", "pointer")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
        }
        onClick { onClick() }
    }) { Text(symbol) }
}

// --- REVEAL -------------------------------------------------------------------------------

@Composable
private fun WebReveal(
    state: OnlineGameState,
    actions: RoomActions,
    room: Room,
    players: List<Player>,
    isHost: Boolean,
    onLeave: () -> Unit,
) {
    val me = state.meIn(players)
    val isInf = me != null && room.infiltratorPlayerIds.contains(me.clientId)
    val accomplices = players
        .filter { room.infiltratorPlayerIds.contains(it.clientId) && it.clientId != me?.clientId }
        .map { it.name }
    val s = I18n.strings
    val readyCount = players.count { it.ready }
    var peeking by remember { mutableStateOf(false) }

    Div({ classes("screen-body") }) {
        GameTopBar(s.revealTopBarReady(readyCount, players.size), onLeave)

        Div({
            style {
                property("flex", "1")
                property("display", "flex")
                property("margin", "16px 0")
            }
        }) {
            Div({
                style {
                    property("width", "100%")
                    property("min-height", "380px")
                    property(
                        "background",
                        when {
                            !peeking -> "var(--card-elevated)"
                            isInf -> "var(--gradient-infiltrator)"
                            else -> "var(--gradient-common)"
                        },
                    )
                    if (!peeking) property("border", "2px dashed var(--border)")
                    property("border-radius", "var(--radius-card)")
                    property("padding", "24px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("text-align", "center")
                    property("cursor", "pointer")
                    property("user-select", "none")
                    property("-webkit-user-select", "none")
                    property("-webkit-touch-callout", "none")
                    property("touch-action", "none")
                }
                // SEGURAR para espiar (mouse + touch); solta/cancela = esconde.
                onMouseDown { peeking = true }
                onMouseUp { peeking = false }
                onMouseLeave { peeking = false }
                onTouchStart { peeking = true }
                onTouchEnd { peeking = false }
                onTouchCancel { peeking = false }
                onContextMenu { it.preventDefault() }
            }) {
                if (!peeking) {
                    Div({ style { property("font-size", "48px") } }) { Text("🤫") }
                    CardText(s.revealSecretCardCover, 15, 500, "var(--muted)", letterSpacing = "4px", marginTop = 12)
                    CardText(s.revealHoldToPeek, 16, 700, "var(--primary)", marginTop = 10)
                    CardText(s.revealOnlyYouCanSee, 13, 400, "var(--muted)", marginTop = 6)
                } else if (isInf) {
                    CardText(s.youAre, 15, 500, "rgba(255,255,255,0.85)", letterSpacing = "4px")
                    CardText(s.infiltrator, 34, 900, "var(--on-danger)", letterSpacing = "1px", marginTop = 6)
                    CardText(s.hintLabel, 13, 500, "rgba(255,255,255,0.8)", letterSpacing = "2px", marginTop = 24)
                    CardText(room.hint ?: "", 22, 700, "var(--on-danger)", marginTop = 4)
                    if (accomplices.isNotEmpty()) {
                        CardText(
                            if (accomplices.size == 1) s.yourAccompliceSingular else s.yourAccomplicesPlural,
                            13, 500, "rgba(255,255,255,0.8)", letterSpacing = "1px", marginTop = 24,
                        )
                        CardText(accomplices.joinToString(" · "), 18, 700, "var(--on-danger)", marginTop = 4)
                    }
                } else {
                    CardText(s.yourWord, 15, 500, "rgba(255,255,255,0.85)", letterSpacing = "4px")
                    CardText(room.word ?: "", 36, 900, "var(--foreground)", letterSpacing = "1px", marginTop = 12)
                    CardText(s.themeWithEmoji(room.themeEmoji, room.themeName), 14, 500, "rgba(255,255,255,0.85)", marginTop = 20)
                }
            }
        }

        Div({ classes("stack") }) {
            PrimaryButton(
                text = if (me?.ready == true) s.readyConfirmed else s.imReady,
                enabled = me?.ready != true && !actions.busy,
            ) { actions.runAction { state.setReady() } }

            if (isHost) {
                val allReady = readyCount == players.size && players.isNotEmpty()
                SecondaryButton(
                    text = if (allReady) s.startDiscussion else s.waitingNReady(players.size - readyCount),
                    enabled = allReady && !actions.busy,
                ) { actions.runAction { state.startTimer(room) } }
            }
        }
        VSpace(16)
    }
}

@Composable
private fun CardText(
    text: String,
    size: Int,
    weight: Int,
    color: String,
    letterSpacing: String? = null,
    marginTop: Int = 0,
) {
    Div({
        style {
            property("font-size", "${size}px")
            property("font-weight", "$weight")
            property("color", color)
            if (letterSpacing != null) property("letter-spacing", letterSpacing)
            if (marginTop > 0) property("margin-top", "${marginTop}px")
        }
    }) { Text(text) }
}

// --- TIMER ---------------------------------------------------------------------------------

@Composable
private fun WebTimer(state: OnlineGameState, actions: RoomActions, room: Room, isHost: Boolean, onLeave: () -> Unit) {
    val s = I18n.strings
    var now by remember { mutableStateOf(nowMillis()) }
    LaunchedEffect(Unit) {
        while (true) {
            delay(250) // ~4x/s
            now = nowMillis()
        }
    }

    val started = room.timerStartedAt ?: now
    val elapsed = ((now - started) / 1000).toInt()
    val remaining = (room.timerSeconds - elapsed).coerceAtLeast(0)
    val frac = if (room.timerSeconds == 0) 0f else remaining.toFloat() / room.timerSeconds

    // Dispara a transição para a votação apenas uma vez, e somente no host.
    // Se falhar, o banner de erro aparece e o botão "Ir para votação" serve de retry.
    var advanced by remember { mutableStateOf(false) }
    LaunchedEffect(remaining == 0) {
        if (remaining == 0 && isHost && !advanced) {
            advanced = true
            actions.runAction { state.goToVote(room) }
        }
    }

    Div({ classes("screen-body") }) {
        GameTopBar(s.discussionTimeTitle, onLeave)

        Div({
            style {
                property("flex", "1")
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "28px")
            }
        }) {
            // Círculo de progresso (conic-gradient) com o tempo restante no centro.
            val deg = frac * 360f
            Div({
                style {
                    property("width", "240px")
                    property("height", "240px")
                    property("border-radius", "50%")
                    property("background", "conic-gradient(var(--primary) ${deg}deg, var(--secondary) ${deg}deg 360deg)")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                }
            }) {
                Div({
                    style {
                        property("width", "212px")
                        property("height", "212px")
                        property("border-radius", "50%")
                        property("background", "var(--background)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("font-size", "52px")
                        property("font-weight", "900")
                        property("font-variant-numeric", "tabular-nums")
                    }
                }) { Text(formatMmSsOnline(remaining)) }
            }

            Div({
                classes("muted", "text-center")
                style { property("font-size", "15px") }
            }) { Text(s.discussionPrompt) }

            if (isHost) {
                Div({ style { property("width", "220px") } }) {
                    SecondaryButton(s.goToVote, enabled = !actions.busy) {
                        actions.runAction { state.goToVote(room) }
                    }
                }
            }
        }
        VSpace(16)
    }
}

// --- VOTE ----------------------------------------------------------------------------------

@Composable
private fun WebVote(
    state: OnlineGameState,
    actions: RoomActions,
    room: Room,
    players: List<Player>,
    votes: List<Vote>,
    isHost: Boolean,
    onLeave: () -> Unit,
) {
    val s = I18n.strings
    val n = room.numInfiltrators
    val myVote = votes.firstOrNull { it.voterId == state.clientId }
    val hasVoted = myVote != null
    val voters = votes.map { it.voterId }.toSet()
    val completed = players.isNotEmpty() && players.all { voters.contains(it.clientId) }
    val selected = remember { mutableStateListOf<String>() }
    val candidates = players.filter { it.clientId != state.clientId }

    Div({ classes("screen-body") }) {
        GameTopBar(s.voteTitle, onLeave)

        VSpace(12)
        Div({
            classes("text-center")
            style {
                property("font-size", "26px")
                property("font-weight", "900")
            }
        }) { Text(s.voteWhoIsInfiltrator) }
        VSpace(8)
        Div({ classes("muted", "text-center") }) {
            Text(
                if (hasVoted) {
                    s.voteRegisteredWaitingWeb(voters.size, players.size)
                } else {
                    s.voteChoosePlayersOnline(n)
                },
            )
        }

        VSpace(20)
        Div({
            classes("grid2", "grow")
            style { property("align-content", "start") }
        }) {
            candidates.forEach { player ->
                val isSelected = selected.contains(player.clientId)
                val didVote = voters.contains(player.clientId)
                VoteCard(
                    name = player.name,
                    statusText = if (didVote) s.voteStatusVoted else s.voteStatusVoting,
                    statusVoted = didVote,
                    selected = isSelected,
                    enabled = !hasVoted,
                    onClick = {
                        if (!hasVoted) {
                            if (isSelected) {
                                selected.remove(player.clientId)
                            } else if (selected.size < n) {
                                selected.add(player.clientId)
                            }
                        }
                    },
                )
            }
        }

        VSpace(16)
        Div({ classes("stack") }) {
            if (!hasVoted) {
                Div({ classes("muted", "text-center") }) { Text(s.voteSelectedOfNParen(selected.size, n)) }
                PrimaryButton(
                    text = if (actions.busy) s.voteSending else s.voteConfirm,
                    enabled = selected.size == n && !actions.busy,
                ) {
                    actions.runAction { state.castVote(selected.toList()) }
                }
            }
            if (isHost) {
                SecondaryButton(
                    text = if (completed) {
                        s.seeResult
                    } else {
                        s.seeResultPartial(voters.size, players.size)
                    },
                    enabled = !actions.busy,
                ) { actions.runAction { state.showResult(room) } }
            }
        }
        VSpace(16)
    }
}

@Composable
private fun VoteCard(
    name: String,
    statusText: String,
    statusVoted: Boolean,
    selected: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Button({
        classes("card-select")
        if (selected) classes("selected")
        if (!enabled) disabled()
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("gap", "8px")
            if (!enabled && !selected) property("opacity", "0.55")
        }
        onClick { onClick() }
    }) {
        AvatarBadge(name, 44)
        Span({
            style {
                property("font-size", "15px")
                property("font-weight", "600")
            }
        }) { Text(name) }
        Div({
            classes("row")
            style { property("gap", "6px") }
        }) {
            Div({
                style {
                    property("width", "7px")
                    property("height", "7px")
                    property("border-radius", "var(--radius-pill)")
                    property("background", if (statusVoted) "var(--primary)" else "var(--muted)")
                }
            })
            Span({
                style {
                    property("font-size", "11px")
                    property("font-weight", "500")
                    property("letter-spacing", "1px")
                    property("color", if (statusVoted) "var(--primary)" else "var(--muted)")
                }
            }) { Text(statusText) }
        }
    }
}

// --- RESULT --------------------------------------------------------------------------------

@Composable
private fun WebResult(
    state: OnlineGameState,
    actions: RoomActions,
    room: Room,
    players: List<Player>,
    votes: List<Vote>,
    isHost: Boolean,
    onLeave: () -> Unit,
) {
    val s = I18n.strings
    // `players` já vem ordenado por joinOrder (RoomRepository.observePlayers).
    val result = GameLogic.computeResult(players.map { it.clientId }, votes, room.infiltratorPlayerIds)
    fun nameOf(id: String) = players.firstOrNull { it.clientId == id }?.name ?: "?"

    val subtitle = when {
        result.playersWon -> s.resultCaughtAll
        result.caughtAny -> s.resultCaughtSome
        else -> s.resultCaughtNone
    }

    Div({ classes("screen-body", "stack") }) {
        VSpace(8)
        // Banner vitória/derrota.
        Div({
            style {
                property("background", if (result.playersWon) "var(--gradient-win)" else "var(--gradient-lose)")
                property("border-radius", "var(--radius-card)")
                property("padding", "24px")
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "center")
                property("gap", "8px")
                property("text-align", "center")
            }
        }) {
            Div({ style { property("font-size", "56px") } }) { Text(if (result.playersWon) s.resultPlayersWonEmoji else s.resultInfiltratorWonEmoji) }
            Div({
                style {
                    property("font-size", "26px")
                    property("font-weight", "900")
                }
            }) { Text(if (result.playersWon) s.resultPlayersWon else s.resultInfiltratorWon) }
            Div({
                style {
                    property("font-size", "14px")
                    property("opacity", "0.85")
                }
            }) { Text(subtitle) }
        }

        // A palavra.
        CardBox {
            Label(s.resultWordWas)
            VSpace(10)
            Div({
                classes("text-center")
                style {
                    property("font-size", "26px")
                    property("font-weight", "700")
                }
            }) { Text("${room.themeEmoji} ${room.word ?: ""}") }
        }

        // Infiltrado(s) revelado(s).
        CardBox {
            Label(if (room.infiltratorPlayerIds.size == 1) s.resultInfiltratorSingular else s.resultInfiltratorsPlural)
            VSpace(12)
            Div({
                style {
                    property("display", "flex")
                    property("flex-wrap", "wrap")
                    property("gap", "8px")
                    property("justify-content", "center")
                }
            }) {
                room.infiltratorPlayerIds.forEach { id ->
                    Span({
                        classes("pill", "pill-danger")
                        style { property("font-size", "14px") }
                    }) { Text(nameOf(id)) }
                }
            }
        }

        // Apuração nomeada.
        CardBox {
            Label(s.resultVotesLabel)
            VSpace(12)
            Div({ classes("stack") }) {
                result.tally.entries.sortedByDescending { it.value }.forEach { entry ->
                    val isInfiltrator = entry.key in room.infiltratorPlayerIds
                    Div({ classes("row-between") }) {
                        Span({
                            style {
                                property("font-size", "15px")
                                property("font-weight", if (isInfiltrator) "700" else "500")
                                property("color", if (isInfiltrator) "var(--danger)" else "var(--foreground)")
                            }
                        }) { Text(nameOf(entry.key)) }
                        Span({
                            style {
                                property("font-size", "14px")
                                property("color", "var(--muted)")
                            }
                        }) { Text(s.votesCountOnline(entry.value)) }
                    }
                }
            }
        }

        VSpace(4)
        if (isHost) {
            PrimaryButton(
                text = if (actions.busy) s.resultPreparing else s.playAgain,
                enabled = !actions.busy,
            ) { actions.runAction { state.playAgain(room) } }
        }
        SecondaryButton(s.resultLeaveRoom) { onLeave() }
        VSpace(16)
    }
}

// --- Helpers ---------------------------------------------------------------------------------

@Composable
private fun AvatarBadge(name: String, size: Int = 36) {
    Div({
        style {
            property("width", "${size}px")
            property("height", "${size}px")
            property("border-radius", "var(--radius-pill)")
            property("background", "var(--secondary)")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("font-size", "${size / 2}px")
            property("flex-shrink", "0")
        }
    }) { Text(Avatars.forPlayer(name)) }
}

@Composable
private fun VSpace(px: Int) {
    Div({
        style {
            property("height", "${px}px")
            property("flex-shrink", "0")
        }
    })
}

/** "03:00" — espelha o formato das telas online do shared. */
private fun formatMmSsOnline(totalSeconds: Int): String {
    val s = totalSeconds.coerceAtLeast(0)
    val mm = (s / 60).toString().padStart(2, '0')
    val ss = (s % 60).toString().padStart(2, '0')
    return "$mm:$ss"
}

/** true se o browser expõe navigator.share (Web Share API — mobile, contexto seguro). */
private fun canNativeShare(): Boolean = window.navigator.asDynamic().share != null

/** Abre o share sheet nativo com a mensagem. Cancelar/falhar é silencioso. */
private fun nativeShare(text: String) {
    try {
        val data = js("({})")
        data.text = text
        window.navigator.asDynamic().share(data).then(
            { _: dynamic -> },
            { _: dynamic -> }, // usuário cancelou — ok
        )
    } catch (_: Throwable) {
        // share indisponível de fato — sem fallback aqui; canNativeShare() já filtra
    }
}

/** Copia via navigator.clipboard com fallback para execCommand("copy"). */
private fun copyToClipboard(text: String, onResult: (Boolean) -> Unit) {
    val clip = window.navigator.asDynamic().clipboard
    if (clip != null) {
        try {
            clip.writeText(text).then(
                { _: dynamic -> onResult(true) },
                { _: dynamic -> onResult(legacyCopy(text)) },
            )
            return
        } catch (_: Throwable) {
            // sem clipboard API → cai no fallback
        }
    }
    onResult(legacyCopy(text))
}

private fun legacyCopy(text: String): Boolean = try {
    val ta = document.createElement("textarea") as HTMLTextAreaElement
    ta.value = text
    ta.setAttribute("readonly", "")
    ta.style.position = "fixed"
    ta.style.left = "-9999px"
    document.body?.appendChild(ta)
    ta.select()
    val ok = document.asDynamic().execCommand("copy") as? Boolean ?: false
    document.body?.removeChild(ta)
    ok
} catch (_: Throwable) {
    false
}
